import { useState, useRef, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import type { AiMessageDTO, AiThreadDTO } from '@amrita/shared';
import { TopBar } from '@/components/ui/TopBar';
import { Button } from '@/components/ui/Button';
import { Markdown } from '@/components/ui/Markdown';
import { haptic } from '@/lib/tg';
import { Send, Loader2, RotateCcw } from 'lucide-react';

interface AskResp {
  threadId: string;
  userMessage: AiMessageDTO;
  reply: AiMessageDTO;
}

const STARTER_QUESTIONS = [
  'Чем отличается изолят от концентрата протеина?',
  'Как объяснить покупателю зачем нужен креатин?',
  'Клиент говорит «это химия» — как ответить?',
  'Какой протеин посоветовать новичку?',
];

export default function AiChatPage() {
  const [input, setInput] = useState('');
  const [threadId, setThreadId] = useState<string | null>(null);
  const [messages, setMessages] = useState<AiMessageDTO[]>([]);
  const endRef = useRef<HTMLDivElement>(null);
  const qc = useQueryClient();
  const [searchParams] = useSearchParams();
  const prefilledRef = useRef(false);

  // Когда есть ?ctx — открываем чистый чат с предзаполненным вопросом, историю НЕ грузим.
  const hasCtx = Boolean(searchParams.get('ctx'));

  // Гард: историю гидрируем в state ровно один раз (на первом монтировании без ctx).
  // После «Новый диалог» или первой гидрации больше не подтягиваем — иначе затрём
  // оптимистичные сообщения / новый пустой диалог.
  const hydratedRef = useRef(false);
  const [restoreDone, setRestoreDone] = useState(hasCtx);

  // 1) Самый свежий thread пользователя (threads приходят отсортированными createdAt DESC).
  const { data: threads, isLoading: threadsLoading } = useQuery<AiThreadDTO[]>({
    queryKey: ['ai-threads'],
    queryFn: () => api.get<AiThreadDTO[]>('/api/ai/threads'),
    enabled: !hasCtx,
  });

  // Берём последний непустой thread (пустые — артефакт прерванных запросов).
  const latestThread = !hasCtx ? threads?.find((t) => t.messagesCount > 0) ?? null : null;

  // 2) Сообщения этого thread'а — только пока не гидрировали и есть что грузить.
  const { data: history, isLoading: historyLoading } = useQuery<AiMessageDTO[]>({
    queryKey: ['ai-thread-messages', latestThread?.id],
    queryFn: () => api.get<AiMessageDTO[]>(`/api/ai/threads/${latestThread!.id}/messages`),
    enabled: Boolean(latestThread) && !hydratedRef.current,
  });

  // Гидрация истории в локальный state (однократно).
  useEffect(() => {
    if (hydratedRef.current || hasCtx) return;
    if (!latestThread) {
      // Список threads загрузился, восстанавливать нечего — показываем стартовые подсказки.
      if (!threadsLoading) setRestoreDone(true);
      return;
    }
    if (history) {
      const visible = history.filter((m) => m.role !== 'system');
      setMessages(visible);
      setThreadId(latestThread.id);
      hydratedRef.current = true;
      setRestoreDone(true);
      // Прокрутка к концу восстановленной переписки.
      setTimeout(() => endRef.current?.scrollIntoView(), 50);
    }
  }, [latestThread, history, threadsLoading, hasCtx]);

  const ask = useMutation<AskResp, Error, string>({
    mutationFn: (question) =>
      api.post<AskResp>('/api/ai/ask', { question, threadId: threadId ?? undefined }),
    onMutate: (question) => {
      const pendingId = `pending-${Date.now()}`;
      setMessages((prev) => [
        ...prev,
        { id: pendingId, role: 'user', content: question, createdAt: new Date().toISOString() },
      ]);
      setInput('');
      return { pendingId };
    },
    onSuccess: (resp) => {
      setThreadId(resp.threadId);
      setMessages((prev) => [
        ...prev.filter((m) => !m.id.startsWith('pending-')),
        resp.userMessage,
        resp.reply,
      ]);
      qc.invalidateQueries({ queryKey: ['ai-threads'] });
      setTimeout(() => endRef.current?.scrollIntoView({ behavior: 'smooth' }), 50);
    },
    onError: () => {
      setMessages((prev) => [
        ...prev,
        {
          id: `error-${Date.now()}`,
          role: 'assistant',
          content: 'Не удалось получить ответ. Попробуй ещё раз.',
          createdAt: new Date().toISOString(),
        },
      ]);
    },
  });

  useEffect(() => {
    endRef.current?.scrollIntoView();
  }, [messages.length]);

  useEffect(() => {
    if (prefilledRef.current) return;
    const ctx = searchParams.get('ctx');
    if (ctx) {
      setInput(`Расскажи подробнее про: ${ctx}`);
    }
    prefilledRef.current = true;
  }, [searchParams]);

  function submit(e: React.FormEvent) {
    e.preventDefault();
    const q = input.trim();
    if (!q || ask.isPending) return;
    ask.mutate(q);
  }

  function newChat() {
    haptic('light');
    // Помечаем историю как «уже гидрированную», чтобы restore-эффект не подтянул её обратно.
    hydratedRef.current = true;
    setThreadId(null);
    setMessages([]);
    setInput('');
    setRestoreDone(true);
  }

  // Идёт восстановление: список threads ещё грузится, либо мы тянем сообщения найденного thread'а.
  const restoring = !restoreDone && (threadsLoading || (Boolean(latestThread) && historyLoading));

  // Стартовые подсказки — только когда реально нет ни истории, ни активных сообщений
  // и восстановление завершено (или его не было из-за ctx).
  const showStarters = messages.length === 0 && !restoring && !ask.isPending;

  return (
    <div className="safe-bottom min-h-full flex flex-col">
      <TopBar
        title="ИИ-ассистент"
        subtitle={threadId ? 'Активный диалог' : 'Новый диалог'}
        right={
          (messages.length > 0 || threadId) && (
            <button
              type="button"
              onClick={newChat}
              aria-label="Новый диалог"
              title="Новый диалог"
              className="p-1.5 rounded-lg hover:bg-tg-secondaryBg"
            >
              <RotateCcw className="w-5 h-5" />
            </button>
          )
        }
      />

      <div className="flex-1 overflow-y-auto p-4 space-y-3">
        {restoring && (
          <div className="text-tg-hint text-sm text-center py-8 inline-flex items-center gap-2 mx-auto">
            <Loader2 className="w-4 h-4 animate-spin" />
            Загружаю переписку…
          </div>
        )}

        {showStarters && (
          <>
            <div className="text-tg-hint text-sm text-center py-8">
              Задай любой вопрос про спортпит, работу в зале, клиентов или 1С.
              <br />
              Ответ можно переслать коллеге 👇
            </div>
            <div className="flex flex-wrap gap-2 justify-center">
              {STARTER_QUESTIONS.map((q) => (
                <button
                  key={q}
                  type="button"
                  onClick={() => {
                    setInput(q);
                    if (!ask.isPending) ask.mutate(q);
                  }}
                  disabled={ask.isPending}
                  className="text-left text-sm px-3 py-2 rounded-2xl bg-tg-secondaryBg disabled:opacity-50"
                >
                  {q}
                </button>
              ))}
            </div>
          </>
        )}

        {messages.map((m) => (
          <div
            key={m.id}
            className={
              'max-w-[85%] rounded-2xl px-3 py-2 text-sm ' +
              (m.role === 'user'
                ? 'ml-auto bg-brand-orange text-white'
                : 'mr-auto bg-tg-secondaryBg')
            }
          >
            {m.role === 'assistant' ? <Markdown>{m.content}</Markdown> : m.content}
          </div>
        ))}

        {ask.isPending && (
          <div className="mr-auto bg-tg-secondaryBg rounded-2xl px-3 py-2 text-sm inline-flex items-center gap-2">
            <Loader2 className="w-4 h-4 animate-spin" />
            Думаю…
          </div>
        )}
        <div ref={endRef} />
      </div>

      <form onSubmit={submit} className="p-3 flex gap-2 border-t border-tg-secondaryBg bg-tg-bg">
        <input
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder="Спроси ИИ…"
          className="flex-1 h-11 px-3 bg-tg-secondaryBg rounded-xl outline-none"
        />
        <Button variant="primary" type="submit" disabled={!input.trim() || ask.isPending}>
          <Send className="w-4 h-4" />
        </Button>
      </form>
    </div>
  );
}
